/**
 * BasicInfoTab Component
 *
 * Form for editing basic guide profile information:
 * - First Name, Last Name
 * - Email, Phone
 * - Business Name
 * - Bio
 * - Primary Activity
 * - Years of Experience
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-12: 5-Tab Profile Editor (8 SP)
 *
 * Props:
 * - profile: object with current profile data
 * - onChange: function(field, value) - Called when field changes
 * - onSave: function() - Called when save button clicked
 * - isSaving: boolean
 * - errors: object with validation errors
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Save as _Save } from 'lucide-react';

const PRIMARY_ACTIVITIES = [
  'Fishing',
  'Hunting',
  'Hiking',
  'Backpacking',
  'Camping',
  'Rock Climbing',
  'Kayaking',
  'Rafting',
  'Wildlife Photography',
  'Birdwatching',
  'Mountain Biking',
  'Skiing',
  'Snowboarding',
  'Other',
];

export default function BasicInfoTab({
  profile,
  onChange,
  onSave,
  isSaving = false,
  errors = {}
}) {
  const handleChange = (field, value) => {
    onChange(field, value);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Basic Information</h2>
          <p className="mt-1 text-sm text-gray-500">
            Update your personal and business details
          </p>
        </div>

        {/* Personal Information */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* First Name */}
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                value={profile.first_name || ''}
                onChange={(e) => handleChange('first_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                  errors.first_name ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="last_name"
                value={profile.last_name || ''}
                onChange={(e) => handleChange('last_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                  errors.last_name ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={profile.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                value={profile.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>

          {/* Business Name */}
          <div className="mb-4">
            <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-1">
              Business Name
            </label>
            <input
              type="text"
              id="business_name"
              value={profile.business_name || ''}
              onChange={(e) => handleChange('business_name', e.target.value)}
              placeholder="e.g., Mountain Adventures Guiding"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional - Leave blank to use your name
            </p>
          </div>

          {/* Primary Activity */}
          <div className="mb-4">
            <label htmlFor="primary_activity" className="block text-sm font-medium text-gray-700 mb-1">
              Primary Activity <span className="text-red-500">*</span>
            </label>
            <select
              id="primary_activity"
              value={profile.primary_activity || ''}
              onChange={(e) => handleChange('primary_activity', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                errors.primary_activity ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select an activity</option>
              {PRIMARY_ACTIVITIES.map((activity) => (
                <option key={activity} value={activity}>
                  {activity}
                </option>
              ))}
            </select>
            {errors.primary_activity && (
              <p className="mt-1 text-sm text-red-600">{errors.primary_activity}</p>
            )}
          </div>

          {/* Years of Experience */}
          <div>
            <label htmlFor="experience_range" className="block text-sm font-medium text-gray-700 mb-1">
              Years of Experience
            </label>
            <input
              type="number"
              id="experience_range"
              value={profile.experience_range || ''}
              onChange={(e) => handleChange('experience_range', parseInt(e.target.value) || 0)}
              min="0"
              max="50"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Bio */}
        <div>
          <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
            Bio / About You <span className="text-red-500">*</span>
          </label>
          <textarea
            id="bio"
            value={profile.bio || ''}
            onChange={(e) => handleChange('bio', e.target.value)}
            rows={6}
            placeholder="Tell customers about your experience, qualifications, and what makes your services unique..."
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
              errors.bio ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-gray-500">
              Minimum 100 characters recommended
            </p>
            <p className="text-xs text-gray-500">
              {profile.bio?.length || 0} characters
            </p>
          </div>
          {errors.bio && (
            <p className="mt-1 text-sm text-red-600">{errors.bio}</p>
          )}
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <_Save className="w-5 h-5" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

BasicInfoTab.propTypes = {
  profile: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  errors: PropTypes.object,
};
